import { useState } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";

const testimonials = [
  {
    name: "Anja",
    age: 27,
    city: "München",
    profession: "Performance-Marketing-Managerin",
    quote:
      "FINABLE hat mir endlich Klarheit über meine Finanzen gegeben – ich weiß jetzt genau, wo mein Geld arbeitet.",
  },
  {
    name: "Moritz",
    age: 25,
    city: "Kiel",
    profession: "Orthopädietechnikmeister",
    quote:
      "Transparent, sympathisch und auf Augenhöhe – so habe ich Finanzberatung noch nie erlebt.",
  },
  {
    name: "Julia",
    age: 27,
    city: "Hamburg",
    profession: "Personalentwicklerin",
    quote:
      "Mit FINABLE habe ich eine Strategie, die wirklich zu mir passt – renditeorientiert und zukunftssicher.",
  },
  {
    name: "Leife",
    age: 30,
    city: "Hamburg",
    profession: "Unternehmensberater",
    quote:
      "Endlich verstehe ich, was mit meinem Geld passiert – und das ohne komplizierte Fachbegriffe.",
  },
  {
    name: "Ludwig",
    age: 26,
    city: "Berlin",
    profession: "Fotograf",
    quote:
      "Ich fühle mich rundum gut betreut. FINABLE denkt mit – und nicht nur an Provisionen.",
  },
  {
    name: "Max",
    age: 25,
    city: "Hamburg",
    profession: "Fahrradmechaniker",
    quote:
      "Die Beratung war so klar und persönlich, dass ich mich sofort wohlgefühlt habe.",
  },
  {
    name: "Marc",
    age: 30,
    city: "Wien",
    profession: "Fitnesscoach",
    quote:
      "Smart investieren und trotzdem gute Renditen – genau das wollte ich, und FINABLE macht es möglich.",
  },
  {
    name: "Max",
    age: 27,
    city: "Schwerin",
    profession: "Finanzbeamter",
    quote:
      "Schnell, ehrlich, kompetent. Ich kann FINABLE jedem in meinem Umfeld weiterempfehlen.",
  },
  {
    name: "Johannes",
    age: 31,
    city: "Stuttgart",
    profession: "Coach",
    quote:
      "Finanzen waren für mich immer ein Buch mit sieben Siegeln – heute habe ich endlich einen klaren Plan.",
  },
];

const PAGE_SIZE = 3;

export default function Testimonials() {
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(testimonials.length / PAGE_SIZE);
  const start = page * PAGE_SIZE;
  const current = testimonials.slice(start, start + PAGE_SIZE);

  const goPrev = () => setPage((p) => (p - 1 + totalPages) % totalPages);
  const goNext = () => setPage((p) => (p + 1) % totalPages);

  return (
    <section id="kundenstimmen" className="py-20 md:py-28 bg-secondary/40">
      <div className="container">
        <div className="max-w-3xl mx-auto text-center reveal">
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-primary text-balance">
            Das berichten begeisterte Menschen über ihre Erfahrungen mit uns.
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Unsere Kunden schätzen unsere renditeorientierte Finanzberatung und die erzielten Ergebnisse.
          </p>
        </div>

        <div className="relative mt-14">
          <button
            type="button"
            onClick={goPrev}
            aria-label="Vorherige Kundenstimmen"
            className="hidden md:flex absolute -left-4 lg:-left-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-card border border-border shadow-card hover:shadow-elegant text-primary items-center justify-center transition-all hover:bg-background"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <div className="grid md:grid-cols-3 gap-6">
            {current.map((t, i) => (
              <article
                key={`${page}-${i}`}
                className="rounded-2xl bg-card border border-border p-6 md:p-8 shadow-card hover:shadow-elegant transition-all animate-fade-in"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="flex gap-1 text-accent">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <blockquote className="mt-4 text-base md:text-lg text-foreground/90 leading-relaxed italic">
                  „{t.quote}"
                </blockquote>
                <div className="mt-6 pt-5 border-t border-border">
                  <div className="font-semibold text-primary">
                    {t.name}, {t.age}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {t.city} · {t.profession}
                  </div>
                </div>
              </article>
            ))}
          </div>

          <button
            type="button"
            onClick={goNext}
            aria-label="Nächste Kundenstimmen"
            className="hidden md:flex absolute -right-4 lg:-right-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-card border border-border shadow-card hover:shadow-elegant text-primary items-center justify-center transition-all hover:bg-background"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          <div className="mt-8 flex items-center justify-center gap-4 md:hidden">
            <button
              type="button"
              onClick={goPrev}
              aria-label="Vorherige Kundenstimmen"
              className="w-11 h-11 rounded-full bg-card border border-border shadow-card text-primary flex items-center justify-center"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={goNext}
              aria-label="Nächste Kundenstimmen"
              className="w-11 h-11 rounded-full bg-card border border-border shadow-card text-primary flex items-center justify-center"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <div className="mt-6 flex items-center justify-center gap-2">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setPage(i)}
                aria-label={`Seite ${i + 1} der Kundenstimmen`}
                aria-current={i === page}
                className={`h-2 rounded-full transition-all ${
                  i === page ? "w-8 bg-accent" : "w-2 bg-border hover:bg-muted-foreground/40"
                }`}
              />
            ))}
          </div>
        </div>

        <div className="mt-10 text-center reveal">
          <a
            href="/kundenstimmen"
            className="inline-flex items-center gap-1 text-accent font-semibold hover:underline"
          >
            Weitere Kundenstimmen →
          </a>
        </div>
      </div>
    </section>
  );
}
